import { ICampaignAppTech } from "api/types/_campaign";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOption from "components/Common/DropdownOption";
import Hashtag from "components/Common/Hashtag";
import TooltipCustom from "components/Common/TooltipCustom";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import { APP_TECH_TYPE, UPLOAD_ACTION } from "helpers/constans";
import moment from "moment";
import React, { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import 'react-json-pretty/themes/monikai.css';
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { postCampaignAppTech, putCampaignAppTech } from "store/campaign/thunk";
import { postUploadFileMulti } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    isOpen?: boolean;
    detail?: ICampaignAppTech | null,
    appTechs?: { label: string, value: string, name: string, type: number }[],
    partners?: { label: string, value: string, name: string, partner_codes?: any[], partner_reporters?: any[] }[],
    onClose?: () => void
    triggerRefresh?: (type?: string) => void;
}

const FILE_SIZE = 11 * 1024 * 1024; // 11MB
const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

registerLocale("en", en);
registerLocale("ko", ko);

const CampaignForm = ({
    isOpen = false,
    detail = null,
    appTechs = [],
    partners = [],
    onClose,
    triggerRefresh
}: Props) => {
    const { t, i18n } = useTranslation();

    const APP_TECH_OPTIONS_LANG: any = appTechs;

    const MEDIA_OPTIONS_LANG: any = partners;

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);

    const onCloseClick = () => {
        onClose && onClose();
    }

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            let res_files: any = [];
            if (values?.files?.length) {
                const res: any = await postUploadFileMulti({ files: values?.files, action: UPLOAD_ACTION.APPTECH });
                res_files = (res?.data || [])?.map((item: any) => (item?.filepath)); // filepath
            };
            const _image_urls: string[] = (values?.image_urls || [])?.map((img: any) => img?.filepath);
            const _emails: string[] = (values?.emails || [])?.map((e: any) => e?.text);
            // const _reporter_create_new = values?.media_reporter_id ? ({
            //     media_reporter_id: values?.media_reporter_id || '',
            //     media_reporter_name: values?.media_reporter_name || values?.media_reporter_id,
            // }) : {};

            const data = {
                name: '',
                ...(!!detail?.id ? {} : { apptech_id: Number(values?.apptech_id?.value) }),
                medias: (values?.media_ids || [])?.map((v: any, index: number) => ({
                    media_id: Number(v?.value),
                    media_code_id: Number((values?.section_ids || [])?.[index]?.value),
                    media_reporter_id: (values?.reporter_ids || [])?.[index] || '',
                    reporter_by_line: (values?.reporter_by_lines || [])?.[index] || '',
                    is_auto_publish: Number((values?.is_auto_publishes || [])?.[index]),
                })) || [],
                times: (values?.times || [])?.map((_time: any) => {
                    if (String(values?.apptech_id?.type) === String(APP_TECH_TYPE.AUTOMATIC)) {
                        return ({
                            time_ranges: [
                                {
                                    start_time: _time?.start_time,
                                    end_time: _time?.end_time,
                                }
                            ],
                            interval: Number(_time?.interval || 0)
                        });
                    } else {
                        return ({ time: _time?.start_time });
                    }
                }),
                image_urls: [...(_image_urls), ...(res_files || [])],
                emails: _emails,
                is_content_paragraph: Number(values?.is_content_paragraph || 0),
            };
            const response: any = !!detail?.id ? await putCampaignAppTech(detail?.id, data) : await postCampaignAppTech(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh(!!values?.is_show_create_reporter ? 'REFRESH_MEDIA' : '');
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({

        name: Yup.string().nullable(),

        apptech_id: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
            type: Yup.number().nullable(),
        }).required(`${t("This is required")}`),

        media_ids: Yup.array().of(Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).required(`${t("This is required")}`)).required(`${t("This is required")}`),

        section_ids: Yup.array().of(Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).required(`${t("This is required")}`)).required(`${t("This is required")}`),

        reporter_ids: Yup.array().of(Yup.string().nullable()).nullable(),

        reporter_by_lines: Yup.array().of(Yup.string().nullable()).nullable(),

        is_content_paragraph: Yup.number().required(`${t("This is required")}`),

        is_auto_publishes: Yup.array().of(Yup.number().required(`${t("This is required")}`)).required(`${t("This is required")}`),

        image_urls: Yup.array().of(Yup.object().shape({
            filepath: Yup.string(),
            url: Yup.string(),
        })).nullable(),

        files: Yup.array()
            .of(
                Yup.mixed()
                    .test(
                        'fileSize',
                        t('File too large. Max size is 10MB.'),
                        (value: any) => !value || (value?.size <= FILE_SIZE)
                    )
                    .test(
                        'fileFormat',
                        t('Unsupported Format. Please upload an Image file.'),
                        (value: any) => !value || SUPPORTED_FORMATS.includes(value?.type)
                    )
            )
            .when("image_urls", {
                is: (image_urls: any) => !image_urls || image_urls?.length === 0,
                then: (schema) => schema.min(1, t('Please select at least 1 image')),
                otherwise: (schema) => schema.notRequired(),
            }),

        emails: Yup.array().of(
            Yup.object().shape({
                id: Yup.string(),
                text: Yup.string().email(t('Invalid email format'))
            })
        ).nullable(),
        is_auto_publish: Yup.number().nullable(),
        times: Yup.array().of(
            Yup.object().shape({
                start_time: Yup.string().nullable(),
                end_time: Yup.string().nullable(),
                interval: Yup.string().nullable(),
            })
        ).nullable(),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            apptech_id: null,
            media_ids: null,
            section_ids: null,
            reporter_ids: null,
            reporter_by_lines: null,
            is_content_paragraph: 0,
            is_auto_publishes: null,
            image_urls: [],
            files: [],
            emails: [],
            is_auto_publish: 0,
            times: []
        },
        validationSchema,
        onSubmit: handleSubmit,
    });


    const handleAddTime = (index: number) => {
        const timeArray = [...(formik.values.times || []), (formik.values.times || [])[(formik.values.times || [])?.length - 1] || { start_time: '09:00', end_time: '20:00', interval: '10' }];
        formik.setFieldValue("times", timeArray);
    };

    const handleRemoveTime = (index: number) => {
        const timeArray = (formik.values.times || [])?.filter((_m, ind: number) => ind !== index);
        formik.setFieldValue("times", timeArray);
    };

    const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            formik.setFieldValue("files", [...formik.values.files, ...fileArray]);
        } else {
            formik.setFieldValue("files", []);
        }
    };

    const handleDeleteFile = (index: number) => {
        if (isLoading) {
            return;
        }
        const fileArray = formik.values.files?.filter((f, i) => i !== index);
        formik.setFieldValue("files", fileArray);
    }

    const handleDeleteImageUrl = (index: number) => {
        if (isLoading) {
            return;
        }
        const imageArray = formik.values.image_urls?.filter((f, i) => i !== index);
        formik.setFieldValue("image_urls", imageArray);
    }

    const handleAddMedia = (index: number) => {
        if (isLoading) {
            return;
        }
        const mediaArray = [...(formik.values.media_ids || []), MEDIA_OPTIONS_LANG[0]];
        const sectionArray = [...(formik.values.section_ids || []), {
            value: String(MEDIA_OPTIONS_LANG[0]?.partner_codes?.[0]?.id),
            label: MEDIA_OPTIONS_LANG[0]?.partner_codes?.[0]?.section_title,
        }];
        const reporterArray = [...(formik.values.reporter_ids || []), {
            value: String(MEDIA_OPTIONS_LANG[0]?.partner_reporters?.[0]?.reporter_id),
            label: MEDIA_OPTIONS_LANG[0]?.partner_reporters?.[0]?.reporter_name,
        }];
        const autoPublishArray = [...(formik.values.is_auto_publishes || []), 1];
        formik.setFieldValue("media_ids", mediaArray);
        formik.setFieldValue("section_ids", sectionArray);
        formik.setFieldValue("reporter_ids", reporterArray);
        formik.setFieldValue("is_auto_publishes", autoPublishArray);
    };

    const handleRemoveMedia = (index: number) => {
        if (isLoading) {
            return;
        }
        const mediaArray = (formik.values.media_ids || [])?.filter((_m, ind: number) => ind !== index);
        const sectionArray = (formik.values.section_ids || [])?.filter((_m, ind: number) => ind !== index);
        const reporterArray = (formik.values.reporter_ids || [])?.filter((_m, ind: number) => ind !== index);
        const autoPublishArray = (formik.values.is_auto_publishes || [])?.filter((_m, ind: number) => ind !== index);;
        formik.setFieldValue("media_ids", mediaArray);
        formik.setFieldValue("section_ids", sectionArray);
        formik.setFieldValue("reporter_ids", reporterArray);
        formik.setFieldValue("is_auto_publishes", autoPublishArray);
    };

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                if (!!detail?.id) {
                    const _name = detail?.name || '';
                    const _apptech = APP_TECH_OPTIONS_LANG?.find((_a: any) => String(detail?.apptech?.id) === String(_a?.value)) || { value: String(detail?.apptech?.id), label: detail?.apptech?.name };
                    const _media_ids = detail?.medias?.map((m: any) => (String(m?.id)));
                    const _medias_v = MEDIA_OPTIONS_LANG?.filter((_m: any) => (_media_ids || [])?.includes(String(_m?.value)));
                    const _medias = !!_medias_v?.length ? _medias_v : [{}];

                    const _sections = !!_medias_v?.length ? detail?.medias?.map((_m: any) => ({
                        label: _m?.media_code?.section_title,
                        value: String(_m?.media_code?.id)
                    })) : [{}];

                    const _reporters = !!_medias_v?.length ? detail?.medias?.map((_m: any) => String(_m?.media_reporter_id || '')) : [''];

                    const _reporter_by_lines = !!_medias_v?.length ? detail?.medias?.map((_m: any) => String(_m?.reporter_by_line || '')) : [''];

                    const _image_urls = detail?.images || [];
                    const _emails = (detail?.emails || [])?.map((e: string) => ({ id: e, text: e }));
                    const _is_auto_publishes = detail?.medias?.map((_m: any) => (Number(_m?.is_auto_publish || 0)));
                    const _is_content_paragraph = Number(detail?.is_content_paragraph || 0);

                    const _times = (detail?.campaign_schedules || [])?.map((_s: any) => {
                        const _times = (String(_apptech?.type) === String(APP_TECH_TYPE.AUTOMATIC)) ? (_s?.valid_time_ranges || []) : [{ start_time: _s?.time }];
                        return ({
                            interval: _s?.interval || 0,
                            start_time: _times[0] ? (`${String(_times[0]?.start_time || '')?.split(':')[0] || ''}:${String(_times[0]?.start_time || '')?.split(':')[1] || ''}`) : '',
                            end_time: _times[0] ? (`${String(_times[0]?.end_time || '')?.split(':')[0] || ''}:${String(_times[0]?.end_time || '')?.split(':')[1] || ''}`) : '',
                        })
                    });
                    setTimeout(() => {
                        formik.setFieldValue('name', _name);
                        formik.setFieldValue('apptech_id', _apptech);
                        formik.setFieldValue('media_ids', _medias);
                        formik.setFieldValue('section_ids', _sections);
                        formik.setFieldValue('reporter_ids', _reporters);
                        formik.setFieldValue('reporter_by_lines', _reporter_by_lines);
                        formik.setFieldValue('image_urls', _image_urls);
                        formik.setFieldValue('files', []);
                        formik.setFieldValue('emails', _emails);
                        formik.setFieldValue('is_auto_publishes', _is_auto_publishes);
                        formik.setFieldValue('is_content_paragraph', _is_content_paragraph);
                        formik.setFieldValue('times', _times);
                    }, 300);
                } else {
                    formik.setFieldValue('name', '');
                    formik.setFieldValue('apptech_id', null);
                    formik.setFieldValue('media_ids', [MEDIA_OPTIONS_LANG[0]]);
                    formik.setFieldValue('section_ids', [
                        {
                            value: String(MEDIA_OPTIONS_LANG[0]?.partner_codes?.[0]?.id),
                            label: MEDIA_OPTIONS_LANG[0]?.partner_codes?.[0]?.section_title,
                        }
                    ]);
                    formik.setFieldValue('reporter_ids', ['']); // String(MEDIA_OPTIONS_LANG[0]?.partner_reporters?.[0]?.reporter_id || '')
                    formik.setFieldValue('reporter_by_lines', ['']);
                    formik.setFieldValue('is_auto_publishes', [0]);
                    formik.setFieldValue('is_content_paragraph', 0);
                    formik.setFieldValue('image_urls', []);
                    formik.setFieldValue('files', []);
                    formik.setFieldValue('emails', []);
                    formik.setFieldValue('times', []);
                    setTimeout(() => {
                        formik.setErrors({});
                    }, 300);
                }
            } catch (error: any) {
                // setIsLoadingDetail((_prev) => false);

                return error;
            }
        };
        handleCallAllOption();
    }, [JSON.stringify(detail), JSON.stringify(MEDIA_OPTIONS_LANG)]);


    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="lg"
                className={`border-header-footer`}
                scrollable={false}
                toggle={onCloseClick}
                keyboard={true}
            >
                <ModalHeader toggle={onCloseClick} className="modal-title-flex-grow-1">
                    <div className="d-flex flex-column gap-0 gap-sm-2 fs-18 fs-md-20">
                        <div className="flex-grow-1">
                            <>{!!detail?.id ? t('Button Update Campaign') : t('Button Create Campaign')}</>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="position-relative">
                    <div>
                        <form onSubmit={formik.handleSubmit}>
                            <Row>
                                <Col sm={12} className="mb-3">
                                    <Label htmlFor="apptech_id" className="form-label">{t('App')} <span className="text-danger"> *</span></Label>
                                    <DropdownOption
                                        name="apptech_id"
                                        dataList={APP_TECH_OPTIONS_LANG}
                                        placeholder={`${t("App")}...`}
                                        className="search-filter-category-type dropdown-status-rounded"
                                        classNamePrefix="name-prefix"
                                        initialValue={formik?.values?.apptech_id || null}
                                        onChangeSelect={(e: any) => {
                                            formik.setFieldValue('apptech_id', e)
                                        }}
                                        isHasOptionAll={false}
                                        isHaveMoreInfo={true}
                                        disabled={isDetailLoading || !!detail?.id}
                                    />
                                    {formik.touched.apptech_id && formik.errors.apptech_id ? (
                                        <div className="text-danger mt-2">{formik.errors.apptech_id}</div>
                                    ) : null}
                                </Col>
                                {
                                    (formik?.values?.media_ids || [])?.map((_m: any, indx: number) => (
                                        <div className="d-flex">
                                            <Row className="flex-grow-1">
                                                <Col sm={6} md={3} className="mb-3 position-relative">
                                                    <Label htmlFor={`media_id_${indx}`} className="form-label">{t('Media')}<span className="text-danger"> *</span> </Label>
                                                    <DropdownOption
                                                        name={`media_id_${indx}`}
                                                        dataList={MEDIA_OPTIONS_LANG}
                                                        placeholder={`${t("Media")}...`}
                                                        className="search-filter-category-type dropdown-status-rounded"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={_m || null}
                                                        isHaveMoreInfo={true}
                                                        onChangeSelect={(e: any) => {
                                                            formik.setFieldValue(`media_ids[${indx}]`, e)
                                                        }}
                                                        isHasOptionAll={false}
                                                        disabled={isDetailLoading}
                                                    />
                                                    {(formik.touched.media_ids as any)?.[indx] && formik.errors.media_ids?.[indx] ? (
                                                        <div className="text-danger mt-2">{formik.errors.media_ids?.[indx]}</div>
                                                    ) : null}
                                                </Col>
                                                <Col sm={6} md={3} className="mb-3 position-relative">
                                                    <Label htmlFor={`section_id_${indx}`} className="form-label">{t('Section')}<span className="text-danger"> *</span> </Label>
                                                    <DropdownOption
                                                        name={`section_id_${indx}`}
                                                        dataList={(_m?.partner_codes || [])?.map((_s: any) => ({ value: String(_s?.id), label: _s?.section_title }))}
                                                        placeholder={`${t("Section")}...`}
                                                        className="search-filter-category-type dropdown-status-rounded"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={((formik?.values?.section_ids as any) || [])[indx] || null}
                                                        onChangeSelect={(e: any) => {
                                                            formik.setFieldValue(`section_ids[${indx}]`, e)
                                                        }}
                                                        isHasOptionAll={false}
                                                        disabled={isDetailLoading}
                                                    />
                                                    {(formik.touched.section_ids as any)?.[indx] && formik.errors.section_ids?.[indx] ? (
                                                        <div className="text-danger mt-2">{formik.errors.section_ids?.[indx] || ''}</div>
                                                    ) : null}
                                                </Col>
                                                <Col sm={6} md={3} className="mb-3 position-relative">
                                                    <Label htmlFor={`reporter_id_${indx}`} className="form-label">{t('Reporter ID')}</Label>
                                                    <Input
                                                        name={`reporter_id_${indx}`}
                                                        type="text"
                                                        className="form-control"
                                                        id={`reporter_id_${indx}`}
                                                        placeholder={t('Reporter ID')}
                                                        value={((formik?.values?.reporter_ids as any) || [])[indx] || ''}
                                                        onChange={(event: any) => formik.setFieldValue(`reporter_ids[${indx}]`, event?.target?.value)}
                                                    />
                                                    {(formik.touched.reporter_ids as any)?.[indx] && formik.errors.reporter_ids?.[indx] ? (
                                                        <div className="text-danger mt-2">{formik.errors.reporter_ids?.[indx] || ''}</div>
                                                    ) : null}
                                                </Col>
                                                <Col sm={6} md={3} className="mb-3 position-relative">
                                                    <Label htmlFor={`reporter_by_line_${indx}`} className="form-label">{t('Byline')}</Label>
                                                    <Input
                                                        name={`reporter_by_line_${indx}`}
                                                        type="text"
                                                        className="form-control"
                                                        id={`reporter_by_line_${indx}`}
                                                        placeholder={t('Byline')}
                                                        value={((formik?.values?.reporter_by_lines as any) || [])[indx] || ''}
                                                        onChange={(event: any) => formik.setFieldValue(`reporter_by_lines[${indx}]`, event?.target?.value)}
                                                    />
                                                    {(formik.touched.reporter_ids as any)?.[indx] && formik.errors.reporter_ids?.[indx] ? (
                                                        <div className="text-danger mt-2">{formik.errors.reporter_ids?.[indx] || ''}</div>
                                                    ) : null}
                                                </Col>

                                                <Col sm={12} md={12} className="mb-3 position-relative align-self-end">
                                                    <div className="form-check form-switch mb-0">
                                                        <Input className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id={`is_auto_publish_${indx}`}
                                                            name={`is_auto_publish_${indx}`}
                                                            checked={!!(((formik?.values?.is_auto_publishes as any) || [])[indx] || 0)}
                                                            onChange={(event: any) => {
                                                                formik.setFieldValue(`is_auto_publishes[${indx}]`, Number(event?.target.checked))
                                                            }}
                                                        />
                                                        <Label htmlFor={`is_auto_publish_${indx}`} className="form-label mb-0">{t('Auto Publication')}
                                                            <TooltipCustom
                                                                title={t("Only campaigns with Auto publication enabled will be sent to NewsHub. If Auto Publication is disabled, the media will not be assigned in Boosting News")}
                                                                className="d-inline-block vertical-align-middle ms-1"
                                                                style={{ transform: 'translateY(1px)' }}
                                                            >
                                                                <i className="ri-question-line align-bottom text-secondary" ></i>
                                                            </TooltipCustom>
                                                        </Label>
                                                    </div>
                                                    {(formik.touched.is_auto_publishes as any)?.[indx] && formik.errors.is_auto_publishes?.[indx] ? (
                                                        <div className="text-danger mt-2">{formik.errors.is_auto_publishes?.[indx] || ''}</div>
                                                    ) : null}
                                                </Col>
                                            </Row>
                                            {/* <div className="d-flex align-items-end justify-content-end mb-3" style={{ width: '60px', paddingBottom: (((formik.touched.media_ids as any)?.[indx] && formik.errors.media_ids?.[indx]) || ((formik.touched.section_ids as any)?.[indx] && formik.errors.section_ids?.[indx])) ? '28px' : '0px' }} >
                                                {(((formik?.values?.media_ids as any)?.length > 1) && (formik?.values?.media_ids as any)?.length !== (indx + 1)) && <button type="button" className="btn btn-md btn-soft-danger edit-item-btn"
                                                    onClick={(e: any) => { e.stopPropagation(); handleRemoveMedia(indx); }}
                                                ><i className="ri-close-fill align-bottom"></i></button>
                                                }
                                                {((formik?.values?.media_ids as any)?.length === (indx + 1)) && <button type="button" className="btn btn-md btn-soft-success edit-item-btn ms-3"
                                                    onClick={(e: any) => { e.stopPropagation(); handleAddMedia(indx); }}
                                                ><i className="ri-add-fill align-bottom"></i></button>}
                                            </div> */}
                                        </div>
                                    ))
                                }
                                {
                                    (formik.values.times || [])?.map((_t: any, indx: number) => (
                                        <Col sm={12} key={`${_t?.start_time}_${indx}`}>
                                            <Row >
                                                <Col sm={4} md={3} lg={3} className="mb-3">
                                                    <div>
                                                        <label className="form-label">
                                                            {indx + 1}. {t("Schedule")} {(String((formik?.values?.apptech_id as any)?.type) === String(APP_TECH_TYPE.AUTOMATIC)) ? (t('Start')) : ''}
                                                        </label>
                                                        <div className="date-picker-wrapper-custom">
                                                            <DatePicker
                                                                className="form-control search"
                                                                placeholderText={`${t("Start")}...`}
                                                                showTimeSelect
                                                                showTimeSelectOnly
                                                                timeIntervals={5}
                                                                autoComplete="off"
                                                                isClearable={true}
                                                                timeCaption={t("Time")}
                                                                timeFormat="HH:mm"
                                                                value={_t?.start_time}
                                                                locale={i18n?.language === "ko" ? "ko" : "en"}
                                                                name={`start_time_${indx}`}
                                                                onChange={(value: Date | null) => {
                                                                    formik.setFieldValue(`times[${indx}].start_time`, value ? moment(new Date(value)).format("HH:mm") : "");
                                                                }}
                                                            />
                                                        </div>
                                                        {(formik.touched.times as any)?.[indx] && (formik.errors.times?.[indx] as any)?.start_time ? (
                                                            <div className="text-danger mt-2">{(formik.errors.times?.[indx] as any)?.start_time || ''}</div>
                                                        ) : null}
                                                    </div>
                                                </Col>
                                                {(String((formik?.values?.apptech_id as any)?.type) === String(APP_TECH_TYPE.AUTOMATIC)) &&
                                                    <>
                                                        <div className="d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0" style={{ width: '20px' }}>
                                                            <span className="fs-20 pt-0 pt-sm-3 text-muted">~</span>
                                                        </div>
                                                        <Col sm={4} md={3} lg={3} className="mb-3">
                                                            <div>
                                                                <label className="form-label">
                                                                    {t("Schedule")} ({t('End')})
                                                                </label>
                                                                <div className="date-picker-wrapper-custom">
                                                                    <DatePicker
                                                                        className="form-control search"
                                                                        placeholderText={`${t("End")}...`}
                                                                        showTimeSelect
                                                                        showTimeSelectOnly
                                                                        timeIntervals={5}
                                                                        autoComplete="off"
                                                                        isClearable={true}
                                                                        timeCaption={t("Time")}
                                                                        timeFormat="HH:mm"
                                                                        value={_t?.end_time}
                                                                        locale={i18n?.language === "ko" ? "ko" : "en"}
                                                                        name={`end_time_${indx}`}
                                                                        onChange={(value: Date | null) => {
                                                                            formik.setFieldValue(`times[${indx}].end_time`, value ? moment(new Date(value)).format("HH:mm") : "");
                                                                        }}
                                                                    />
                                                                </div>
                                                                {(formik.touched.times as any)?.[indx] && (formik.errors.times?.[indx] as any)?.end_time ? (
                                                                    <div className="text-danger mt-2">{(formik.errors.times?.[indx] as any)?.end_time || ''}</div>
                                                                ) : null}
                                                            </div>
                                                        </Col>
                                                        <Col sm={3} md={3} lg={3} className="mb-3">
                                                            <label className="form-label">
                                                                {t("Interval")} ({t('Minute')})
                                                            </label>
                                                            <Input
                                                                name={`interval_${indx}`}
                                                                type="text"
                                                                className="form-control"
                                                                id={`reporter_id_${indx}`}
                                                                placeholder={t('Interval')}
                                                                value={_t?.interval}
                                                                onChange={(event: any) => {
                                                                    const input = event.target.value;
                                                                    const onlyNumbers = input.replace(/\D/g, "");
                                                                    const s = onlyNumbers;
                                                                    formik.setFieldValue(`times[${indx}].interval`, s);
                                                                }}
                                                            />
                                                            {(formik.touched.times as any)?.[indx] && (formik.errors.times?.[indx] as any)?.interval ? (
                                                                <div className="text-danger mt-2">{(formik.errors.times?.[indx] as any)?.interval || ''}</div>
                                                            ) : null}
                                                        </Col>
                                                    </>}
                                                <div className="d-flex align-items-end justify-content-end justify-content-md-start  mb-3 flex-grow-1" style={{ width: '120px', paddingBottom: (((formik.touched.times as any)?.[indx] && formik.errors.times?.[indx])) ? '28px' : '0px' }} >
                                                    {(((formik?.values?.times as any)?.length === 1) || (formik?.values?.times as any)?.length !== (indx + 1)) && <button type="button" className={`btn btn-md btn-soft-danger edit-item-btn ${((formik?.values?.times as any)?.length === (indx + 1)) ? 'me-3' : ''}`}
                                                        onClick={(e: any) => { e.stopPropagation(); handleRemoveTime(indx); }}
                                                    ><i className="ri-close-fill align-bottom"></i></button>}
                                                    {((formik?.values?.times as any)?.length === (indx + 1)) && <button type="button" className="btn btn-md btn-soft-success edit-item-btn"
                                                        onClick={(e: any) => { e.stopPropagation(); handleAddTime(indx); }}
                                                    ><i className="ri-add-fill align-bottom"></i></button>}
                                                </div>
                                            </Row>
                                        </Col>
                                    ))
                                }
                                {
                                    (formik.values.times || [])?.length === 0 && (
                                        <div className="d-flex flex-grow-1">
                                            <Col sm={6} md={4} lg={4} className="mb-3">
                                                <label className="form-label">
                                                    {t("Schedule")}
                                                </label>
                                                <div className="d-flex align-items-end justify-content-start mb-3" style={{ width: '45px', paddingBottom: '0px' }} >
                                                    <button type="button" className="btn btn-md btn-soft-success edit-item-btn ms-0"
                                                        onClick={(e: any) => { e.stopPropagation(); handleAddTime(0); }}
                                                    ><i className="ri-add-fill align-bottom"></i></button>
                                                </div>
                                            </Col>

                                        </div>
                                    )
                                }
                                <Col sm={12} md={12} xl={12} className='mb-3'>
                                    <div style={{ minHeight: '80px' }}>
                                        <label className="form-label">
                                            {t("Email")} ({t('Multi')})
                                            <TooltipCustom
                                                title={t("A notification email will be sent each time an article is created.")}
                                                className="d-inline-block vertical-align-middle ms-1"
                                                style={{ transform: 'translateY(1px)' }}
                                            >
                                                <i className="ri-question-line align-bottom text-secondary" ></i>
                                            </TooltipCustom>
                                        </label>
                                        <Hashtag
                                            name="emails"
                                            placeholder={`${t("Press Enter to add a new email")}...`}
                                            suggestions={[]}
                                            initialValue={formik?.values?.emails || []}
                                            onChangeTag={(event: any) => {
                                                formik.setFieldValue("emails", event);
                                            }}
                                            idSelected="hashtags-CampaignFormMulti"
                                            maxHashtags={2000}
                                            maxHeight="150px"
                                        />
                                    </div>
                                    {formik.touched.emails && formik.errors.emails ? (
                                        <div className="text-danger mt-2">{(formik.errors.emails?.[0] as any)?.text || ''}</div>
                                    ) : null}
                                </Col>

                                <Col sm={12} md={12} className="mb-3 position-relative align-self-end">
                                    <div className="form-check form-switch mb-0">
                                        <Input className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id={`is_content_paragraph`}
                                            name={`is_content_paragraph`}
                                            checked={!!(formik?.values?.is_content_paragraph || 0)}
                                            onChange={(event: any) => {
                                                formik.setFieldValue(`is_content_paragraph`, Number(event?.target.checked))
                                            }}
                                        />
                                        <Label htmlFor={`is_content_paragraph`} className="form-label mb-0">{t('Use Content Paragraph')}
                                            <TooltipCustom
                                                title={t("If Use Content Paragraph is enabled, the content will be generated from the content paragraph. If Use Content Paragraph is disabled, the content will be generated from the content.")}
                                                className="d-inline-block vertical-align-middle ms-1"
                                                style={{ transform: 'translateY(1px)' }}
                                            >
                                                <i className="ri-question-line align-bottom text-secondary" ></i>
                                            </TooltipCustom>
                                        </Label>
                                    </div>
                                    {(formik.touched.is_content_paragraph) && formik.errors.is_content_paragraph ? (
                                        <div className="text-danger mt-2">{formik.errors.is_content_paragraph || ''}</div>
                                    ) : null}
                                </Col>

                                <Col sm={12} md={12} xl={12} className="mb-4">
                                    <Label htmlFor="formSizeDefault" className="form-label">{t('Image')} <span className="text-danger"> *</span></Label>
                                    <div className="d-flex gap-3 flex-wrap">
                                        {
                                            (formik.values.image_urls || [])?.map((img: any, index: number) => (
                                                <div className="position-relative" style={{ width: '200px', height: '200px', backgroundColor: '#eaeaea', overflow: 'hidden', borderRadius: 'var(--vz-border-radius)' }}>
                                                    <img
                                                        src={img?.url}
                                                        alt={`preview-${index}`}
                                                        style={{ width: "100%", height: "100%", objectFit: 'cover' }}
                                                    />
                                                    <button type="button" className="position-absolute btn btn-sm btn-soft-danger edit-item-btn"
                                                        style={{
                                                            top: '10px',
                                                            right: '10px',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '15px',
                                                        }}
                                                        onClick={() => handleDeleteImageUrl(index)}
                                                    ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                                                </div>
                                            ))
                                        }
                                        {
                                            (formik.values.files || [])?.map((file: any, index: number) => (
                                                <div className="position-relative" style={{ width: '200px', height: '200px', backgroundColor: '#eaeaea', overflow: 'hidden', borderRadius: 'var(--vz-border-radius)' }}>
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt={`preview-${index}`}
                                                        style={{ width: "100%", height: "100%", objectFit: 'cover' }}
                                                    />
                                                    <button type="button" className="position-absolute btn btn-sm btn-soft-danger edit-item-btn"
                                                        style={{
                                                            top: '10px',
                                                            right: '10px',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '15px',
                                                        }}
                                                        onClick={() => handleDeleteFile(index)}
                                                    ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                                                </div>
                                            ))
                                        }
                                        <div className="custom-file-image-button">
                                            <Input
                                                className="form-control"
                                                accept="image/jpeg, image/png, image/gif, image/webp, image/svg+xml"
                                                label="Upload Your Image"
                                                data-title="Upload Your Image"
                                                id="formSizeDefault"
                                                type="file"
                                                multiple
                                                disabled={isLoading}
                                                onChange={handleChangeFile}
                                            />
                                            {<div className="text-upload" style={{ zIndex: 1 }}>  <i className="ri-upload-line me-2" /> {formik.values.files?.length ? t('Add Image') : t('No file chosen')}</div>}
                                        </div>
                                    </div>
                                    {formik.touched.files && formik.errors.files ? (
                                        <div className="text-danger mt-2">{formik.errors.files}</div>
                                    ) : null}
                                </Col>

                                <Col sm={12} className="mb-2" >
                                    <div className="d-flex gap-3 justify-content-end align-item-center">
                                        <button
                                            className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-item-center"
                                            type="submit"
                                            style={{ width: '160px' }}
                                            disabled={isLoading}
                                        >
                                            {isLoading && <span style={{ transform: 'translateY(1px)' }} className="me-1"><Spinner size="sm"></Spinner></span>}<span>{
                                                !!detail?.id ? t('Button Update Campaign') : t('Button Create Campaign')
                                            }</span>
                                        </button>
                                        <button
                                            className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-item-center"
                                            type="button"
                                            onClick={onCloseClick}
                                            style={{ width: '75px' }}
                                        >
                                            <i className="ri-close-fill" style={{ lineHeight: '22px' }} /> <span className="ms-1">{t("Button Close")}</span>
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment >
    );
};

export default CampaignForm;

